import React from "react";
import Grid from "@material-ui/core/Grid";
import {
	FormControlLabel,
	InputLabel,
	Radio,
	Toolbar
} from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import { Container, PrimaryButton } from "@prestosports/commons-ui";
import { useFormik } from "formik";
import * as Yup from "yup";
import RadioGroup from "@material-ui/core/RadioGroup";
import updateFieldSelectorStyles from "../../styles/UpdateFieldSelector.styles";
import { useGameContextContext } from "../../../../state/game-context/context";
import FieldPositionPicker from "../FieldPositionPicker";
import ZoneToGainEditor from "./ZoneToGainEditor";
import { useEventContext } from "../../../../state/events/context";
import useFieldConstants from "../utils/FieldConfiguration";
import SubmitCancelListener from "../listeners/SubmitCancelListener";
import TeamPossession from "./TeamPossession";
import ScoreUpdate from "./ScoreUpdate";

const UpdateFieldSelector = ({
	onSelected = () => {},
	selected = {},
	disableKeyListener
}) => {
	const classes = updateFieldSelectorStyles();

	const { state: gameContextState } = useGameContextContext();
	const homeScore = gameContextState.homeScore();
	const awayScore = gameContextState.visitorScore();
	const teamPossession = gameContextState.teamPossession() || {};
	const fieldPosition = gameContextState.fieldPosition();
	const down = gameContextState.down();
	const firstDownAt = gameContextState.firstDownAt();
	const swapTeams = gameContextState.swapTeams();
	const liveGameConfig = gameContextState.gameConfig();

	const isZoneToGain = liveGameConfig?.general?.firstDownFlagStyle > 0;
	const firstDownLabel = isZoneToGain ? "Zone to Gain" : "Yard to Gain";

	const { state, saveFirstDownYardSet } = useEventContext();
	const currentEventSettings = state.currentEventSettings();
	const { fieldLength, downs } = useFieldConstants(currentEventSettings);

	// The zone-to-gain spot set from the LIVE game config — seeds the editable
	// spots editor (zone-to-gain only). Empty for yard-to-gain / traditional.
	const originalSpots = isZoneToGain
		? liveGameConfig?.general?.firstDownYardSet || []
		: [];

	const sortedKey = (spots) => [...spots].sort((a, b) => a - b).join(",");

	const snapToNearest = (target, options) => {
		return options.length
			? options.reduce((prev, curr) =>
					Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev
			  )
			: target;
	};

	// Snap the initial target to the nearest configured line for zone-to-gain.
	// The live firstDownAt can be stale/off-line (e.g. the game was started as
	// yard-to-gain, then the config was changed to zone-to-gain mid-game).
	const initialFirstDownAt = isZoneToGain
		? snapToNearest(firstDownAt, [0, ...originalSpots, fieldLength])
		: firstDownAt;

	const validationSchema = Yup.object({
		awayScore: Yup.number().required("Required"),
		homeScore: Yup.number().required("Required"),
		down: Yup.number().required("Required"),
		teamPossession: Yup.string().required("Required")
	});

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			teamPossession: teamPossession.id,
			fieldPosition,
			down,
			firstDownAt: initialFirstDownAt,
			firstDownYardSet: originalSpots,
			homeScore,
			awayScore,
			...selected
		},
		validationSchema,
		onSubmit: (values) => {
			// Persist edited zone spots to the event config + live game (bypasses
			// the after-start guard), then save the play fragment. Include the
			// spot arrays on the fragment so the play-list message shows the diff.
			if (isZoneToGain) {
				const spotsChanged =
					sortedKey(values.firstDownYardSet) !== sortedKey(originalSpots);
				if (spotsChanged) {
					saveFirstDownYardSet(values.firstDownYardSet);
				}
				onSelected({
					...values,
					previousFirstDownYardSet: originalSpots
				});
			} else {
				onSelected(values);
			}
		}
	});

	const {
		handleSubmit,
		handleChange,
		handleBlur,
		setFieldValue,
		errors,
		touched
	} = formik;

	const hasErrors = () => {
		return !!Object.keys(errors).length;
	};

	return (
		<Container fullHeight>
			<form className={classes.form} onSubmit={handleSubmit}>
				<Grid container className={classes.mainPanel}>
					<Grid item xs={12}>
						<Grid container direction="column" spacing={4}>
							<Grid item>
								<FormControl variant="outlined" fullWidth>
									<InputLabel shrink>New Ball Spot</InputLabel>
									<FieldPositionPicker
										onChange={(_, value) => setFieldValue("fieldPosition", value)}
										value={formik.values.fieldPosition}
										fieldLength={fieldLength}
										reversed={!swapTeams}
									/>
								</FormControl>
							</Grid>
							<Grid item>
								<FormControl variant="outlined" fullWidth>
									<InputLabel shrink>Down</InputLabel>
									<RadioGroup
										row
										aria-label="down"
										name="down"
										value={formik.values.down.toString()}
										onChange={(e) => {
											setFieldValue("down", Number(e.target.value));
										}}
										className={classes.downOptions}
									>
										{Array.from(Array(downs).keys()).map((k) => {
											const d = `${k + 1}`;
											return (
												<FormControlLabel
													value={d}
													control={<Radio id={`down-radio-btn-${d}`} color="primary" />}
													label={d}
												/>
											);
										})}
									</RadioGroup>
								</FormControl>
							</Grid>
							<Grid item>
								<FormControl variant="outlined" fullWidth>
									<InputLabel shrink>{firstDownLabel}</InputLabel>
									{isZoneToGain ? (
										<ZoneToGainEditor
											spots={formik.values.firstDownYardSet}
											target={formik.values.firstDownAt}
											fieldLength={fieldLength}
											reversed={!swapTeams}
											onSpotsChange={(spots) => setFieldValue("firstDownYardSet", spots)}
											onTargetChange={(value) => setFieldValue("firstDownAt", value)}
										/>
									) : (
										<FieldPositionPicker
											onChange={(_, value) => setFieldValue("firstDownAt", value)}
											value={formik.values.firstDownAt}
											fieldLength={fieldLength}
											reversed={!swapTeams}
										/>
									)}
								</FormControl>
							</Grid>
						</Grid>
					</Grid>
					<Grid className={classes.teamPosessionContainer} item xs={12}>
						<Grid container direction="column" spacing={4}>
							<Grid item>
								<FormControl variant="outlined" fullWidth>
									<InputLabel shrink>Team Possession</InputLabel>
									<TeamPossession
										teamPossession={formik.values.teamPossession}
										setTeamPossession={(value) => setFieldValue("teamPossession", value)}
									/>
								</FormControl>
							</Grid>
							<Grid item>
								<FormControl variant="outlined" fullWidth>
									<InputLabel shrink>Score</InputLabel>
									<ScoreUpdate
										awayScore={formik.values.awayScore}
										homeScore={formik.values.homeScore}
										handleBlur={handleBlur}
										handleChange={handleChange}
										touched={touched}
										errors={errors}
									/>
								</FormControl>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
				<Toolbar disableGutters variant="dense">
					<Grid
						container
						direction="row"
						justifyContent="flex-end"
						alignItems="center"
					>
						<Grid item>
							<PrimaryButton type="submit" label="Save" disabled={hasErrors()} />
						</Grid>
					</Grid>
				</Toolbar>
				{!disableKeyListener && (
					<SubmitCancelListener
						submitDisabled={hasErrors()}
						onSubmit={handleSubmit}
					/>
				)}
			</form>
		</Container>
	);
};

export default UpdateFieldSelector;
